import classnames from 'classnames';
import update from 'immutability-helper';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FilterIcon from '@icons/emp/filter-icon.svg';
import Icon, { CloseOutlined } from '@ant-design/icons';
import FilterNameItem, {
  IRule,
} from '@components/summary-panel/FilterDropdown/FilterName/FilterNameItem';
import { mappedEventDimensionType } from '@src/common/constant/enum';
import { selector as commonSelector } from '@store/common';
import { applyFilter } from '@store/filterContainer/actions';
import { getFilterFromRules, getRulesFromFilter } from '@utils';

import styles from './index.less';
import { useIsNewWorkflowPage } from '@src/hooks/useNewWorkflow';

interface IFilterName {
  onClick?: () => void;
  onRemoveFilter?: () => void;
}

interface IFilterRows extends IRule {
  andIndex: number;
  orIndex: number;
}

// The total length of text is more than 400px, displaying ellipses
const TEXT_DISPLAY_MAX_WIDTH = 400;

const FilterName: React.FC<IFilterName> = ({ onClick }) => {
  const dispatch = useDispatch();
  const { appliedFilters, filterColor } = useSelector(commonSelector);
  const isEasierEventLooker = useIsNewWorkflowPage();
  const appliedFilter = appliedFilters[0] ?? {};
  const { rule = [], name = '' } = appliedFilter;
  const onlyOneAndFilter = rule.length === 1;
  const onlyOneOrFilter = rule?.[0]?.length === 1;
  const onlyOneFilter = onlyOneAndFilter && onlyOneOrFilter;

  const timeOutRef = useRef<number>();

  const [showLinkIcon, setShowLinkIcon] = useState<boolean>(false);
  const [activeRule, setActiveRule] = useState<IRule | undefined>();
  const [filterRows, setFilterRows] = useState<IFilterRows[][]>([]);

  // Only 1 tag key can show the link icon
  const canShowLinkIcon = useMemo(() => {
    let tagKeyNums = 0;
    appliedFilter?.rule.forEach((andRule) => {
      const tagKeyList = andRule.filter(
        (orRule) => orRule.field === mappedEventDimensionType.merged_event_tag_key,
      );
      tagKeyNums += tagKeyList.length;
    });
    return tagKeyNums === 1;
  }, [appliedFilter]);

  const onMouseOverCloseIcon = ({ rule }) => {
    // Determine whether to display link Icon
    if (rule.field === mappedEventDimensionType.merged_event_tag_key && canShowLinkIcon) {
      setShowLinkIcon(true);
      // Hover "X" more than 2sec to remove one of linked filters
      clearTimeout(timeOutRef.current);
      timeOutRef.current = setTimeout(() => {
        setShowLinkIcon(false);
      }, 2000);
    }
  };

  const onMouseLeaveCloseIcon = ({ rule }) => {
    if (rule.field.indexOf('tag_key') !== -1) {
      setShowLinkIcon(false);
    }
  };

  const onRemoveFilter = ({ e, andIndex, orIndex }) => {
    e?.stopPropagation();
    // delete single orRule
    const ruleAfterDeleteSingle = update(appliedFilter, {
      rule: {
        [andIndex]: {
          $splice: [[orIndex, 1]],
        },
      },
    });
    // [[]] converts to []
    ruleAfterDeleteSingle.rule = ruleAfterDeleteSingle.rule
      .map((andRule) => {
        if (andRule.length !== 0) {
          // delete link filter
          if (
            !(showLinkIcon && andRule.some((orRule) => orRule.field.indexOf('tag_value') !== -1))
          ) {
            return andRule;
          }
        }
      })
      .filter(Boolean);
    setShowLinkIcon(false);
    const tempRules = getRulesFromFilter(ruleAfterDeleteSingle.rule, true);
    ruleAfterDeleteSingle.name = getFilterFromRules(tempRules, true).name;
    dispatch(applyFilter(ruleAfterDeleteSingle));
  };

  const onClearAllFilter = (e) => {
    e?.stopPropagation();
    const filterData = {
      name: 'Filters',
      rule: [],
      is_content: false,
    };
    dispatch(applyFilter(filterData));
  };

  const changeActiveRule = (rule?: IRule) => {
    setActiveRule(rule);
  };

  const getFilterRowMaxWidth = useCallback(() => {
    const AddToRuleButtonWidth = 127 + 16 * 2;
    const filterRowMaxWidth = document.getElementById('emp-filter-row')?.clientWidth || 0;
    return filterRowMaxWidth - AddToRuleButtonWidth;
  }, []);

  const genFilterRows = useCallback(() => {
    const rowMaxWidth = getFilterRowMaxWidth();
    const rows: any[][] = [];
    let tempRow: any[] = [];
    let tempRowLength = 0;

    cloneDeep(appliedFilter).rule?.forEach((andRule, andIndex) => {
      andRule?.forEach((orRule, orIndex) => {
        orRule['andIndex'] = andIndex;
        orRule['orIndex'] = orIndex;
        // fontsize was 24px just try 14 for text length
        const andTextWidth = 46;
        const orTextWidth = 32;
        const closeIconWidth = 22;
        const nameTextWidth =
          orRule.value.length * 14 < TEXT_DISPLAY_MAX_WIDTH
            ? orRule.value.length * 14
            : TEXT_DISPLAY_MAX_WIDTH;
        let filterItemWidth = nameTextWidth + closeIconWidth;
        if (orIndex !== 0) {
          filterItemWidth += orTextWidth;
        } else if (andIndex !== 0 && orIndex === 0) {
          filterItemWidth += andTextWidth;
        }
        tempRowLength += filterItemWidth;

        if (tempRowLength < rowMaxWidth) {
          tempRow.push(orRule);
        } else {
          tempRowLength = filterItemWidth;
          rows.push(tempRow);
          tempRow = [orRule];
        }
      });
    });

    if (tempRow.length) {
      rows.push([...tempRow]);
    }
    setFilterRows(rows);
  }, [getFilterRowMaxWidth, appliedFilter]);

  // Rebuild filter rows when appliedFilters changed
  useEffect(() => {
    genFilterRows();
  }, [appliedFilter]);

  // Rebuild filter rows when window resize
  useEffect(() => {
    window.addEventListener('resize', genFilterRows);
    return () => {
      window.removeEventListener('resize', genFilterRows);
    };
  }, [genFilterRows]);

  return (
    <div className={styles.filterNameWrapper}>
      {isEasierEventLooker ? (
        <Icon component={FilterIcon} onClick={onClick} className={styles.filterIcon} />
      ) : (
        <span
          className={classnames(styles.verticalBar, {
            [styles.verticalBarWithFilter]: appliedFilter?.rule?.length !== 0,
          })}
          style={{ background: filterColor }}
        />
      )}
      <div
        className={classnames(styles.filterName, {
          [styles.filterNameWithHover]: rule.length !== 0,
          [styles.filterNameBlue]: isEasierEventLooker && rule.length === 0,
        })}
        onClick={onClick}
      >
        {rule.length === 0 ? (
          <span className={styles.allText}>{name}</span>
        ) : (
          filterRows.map((row, rowIndex) => {
            return (
              <div
                className={classnames(styles.rowWrapper, {
                  [styles.rowWrapperWithBottom]: rowIndex !== filterRows.length - 1,
                })}
                key={rowIndex}
              >
                {row.map((orRule, orRuleIndex) => {
                  const { andIndex, orIndex } = orRule;
                  const prefix =
                    andIndex !== 0 && orIndex === 0 ? 'AND' : orIndex !== 0 ? 'OR' : undefined;
                  const firstOrFilterUnderAndFilterGroup = orIndex === 0;
                  let isTagValue = false;
                  if (orRule.field === mappedEventDimensionType.merged_event_tag_value) {
                    isTagValue = true;
                  }
                  // Judging whether it is the last rule
                  const isLastRule =
                    filterRows.length - 1 === rowIndex && row.length - 1 === orRuleIndex;
                  return (
                    <div className={styles.filterNameItemWrapper} key={`${andIndex}-${orIndex}`}>
                      <FilterNameItem
                        activeRule={activeRule}
                        showCloseIcon={!onlyOneFilter}
                        firstOrFilterUnderAndFilterGroup={firstOrFilterUnderAndFilterGroup}
                        andIndex={andIndex}
                        orIndex={orIndex}
                        prefix={prefix}
                        rule={orRule}
                        showLinkFilters={showLinkIcon && isTagValue}
                        changeActiveRule={changeActiveRule}
                        onRemoveFilter={onRemoveFilter}
                        onMouseOverCloseIcon={onMouseOverCloseIcon}
                        onMouseLeaveCloseIcon={onMouseLeaveCloseIcon}
                      />
                      {isLastRule && (
                        <span
                          className={classnames(styles.closeAllBtn, styles.closeAllBtnInside, {
                            [styles.closeAllBtnSmall]: isEasierEventLooker,
                          })}
                          onClick={onClearAllFilter}
                        >
                          <CloseOutlined />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
      {rule.length !== 0 && (
        <span
          className={classnames(styles.closeAllBtn, styles.closeAllBtnOutside, {
            [styles.closeAllBtnSmall]: isEasierEventLooker,
          })}
          onClick={onClearAllFilter}
        >
          <CloseOutlined />
        </span>
      )}
    </div>
  );
};

export default FilterName;
